import { AiAssessmentEvaluation, AppAssessmentDetails, EvalGroup } from '@primatoo-ms-organization';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DetailsChartsDto, ND, RiskAssessmentChart, TreatmentChart } from '@pt/pt-models';
import { ImpactMetricValue } from 'it/primatoo/openapi/api/org/schemas/common/ImpactMetricValue';
import {
  RiskEvaluationGroup,
  RiskEvaluationGroup as RiskEvaluationGroupV2
} from 'it/primatoo/openapi/api/org/schemas/treatment/riskassessment/RiskEvaluationGroup';
import { ThreatValue } from 'it/primatoo/openapi/api/org/schemas/treatment/riskassessment/ThreatValue';
import {
  CovidAssessmentEvaluation
} from 'it/primatoo/openapi/api/org/schemas/covidassessment/CovidAssessmentEvaluation';

export class StatsChartBuilder {

  public static buildAppAssessmentStats(appAssessmentDetails: AppAssessmentDetails, translateService: TranslateService): DetailsChartsDto[] {
    const appAssessmentStats: DetailsChartsDto[] = [];
    appAssessmentStats.push({
      label: translateService.stream('LABELS.CONFIDENTIALITY_VIOLATION'),
      textValue: of(appAssessmentDetails?.scenarioRLevelLabel),
      value: appAssessmentDetails?.scenarioRProbValue,
      max: 5
    });
    appAssessmentStats.push({
      label: translateService.stream('LABELS.INTEGRITY_COMPROMISE'),
      textValue: of(appAssessmentDetails?.scenarioILevelLabel),
      value: appAssessmentDetails?.scenarioIProbValue,
      max: 5
    });
    appAssessmentStats.push({
      label: translateService.stream('LABELS.AVAILABILITY_LOSS'),
      textValue: of(appAssessmentDetails?.scenarioDLevelLabel),
      value: appAssessmentDetails?.scenarioDProbValue,
      max: 5
    });
    return appAssessmentStats;
  }

  public static buildRiskAssessmentStats(evaluations: RiskEvaluationGroup[], evaluationLabels: string[]): RiskAssessmentChart[] {
    const riskAssessmentStats: RiskAssessmentChart[] = [];
    if (evaluations && evaluations?.length) {
      evaluations.forEach((groups: RiskEvaluationGroup) => {
        const charts: DetailsChartsDto[] = [];
        groups.threats.forEach((threatValue: ThreatValue) => {
          const key = threatValue?.threat;
          const value = threatValue?.value !== null && threatValue?.value !== undefined && threatValue?.value !== -1 ? threatValue?.value : 0 ;
          const textValue = (value !== -1 && evaluationLabels?.length > value) ? evaluationLabels[value] : ND;

          charts.push({
            label: of(key),
            textValue: of(textValue),
            value: value,
            max: 5
          });
        })
        riskAssessmentStats.push({
          label: groups.group,
          charts: charts
        });
      });
    }
    return riskAssessmentStats;
  }

  public static buildRiskAssessmentStatsV2(evaluations: RiskEvaluationGroupV2[]): RiskAssessmentChart[] {
    const riskAssessmentStats: RiskAssessmentChart[] = [];
    if (evaluations && evaluations.length) {
      evaluations.forEach((groups: RiskEvaluationGroupV2) => {
        const charts: DetailsChartsDto[] = [];
        groups.threats.forEach((threatValue: ThreatValue) => {
          const key = threatValue?.threat;
          const value = threatValue?.value !== null && threatValue?.value !== undefined && threatValue?.value !== -1 ? threatValue?.value : 0 ;
          const textValue = threatValue?.valueLabel || ND;

          charts.push({
            label: of(key),
            textValue: of(textValue),
            value: value,
            max: 5
          });
        })
        riskAssessmentStats.push({
          label: groups.group,
          charts: charts
        });
      });
    }
    return riskAssessmentStats;
  }

  public static buildTreatmentStats(appsAuditEvaluation?: Array<ImpactMetricValue>, evaluationLabels?: Array<string>): TreatmentChart[] {
    const treatmentCharts: TreatmentChart[] = [];
    if (appsAuditEvaluation && appsAuditEvaluation.length && evaluationLabels && evaluationLabels.length) {
      const charts: DetailsChartsDto[] = [];
      appsAuditEvaluation.forEach((threatValue) => {
        const key = threatValue?.label;
        const value = threatValue?.value !== null && threatValue?.value !== undefined && threatValue?.value !== -1 ? threatValue?.value : 0 ;
        const textValue = evaluationLabels.length > value && evaluationLabels[value] ? evaluationLabels[value] : ND;

        charts.push({
          label: of(key),
          textValue: of(textValue || ND),
          value: value,
          max: 5
        });
      });
      treatmentCharts.push({
        label: 'LABELS.CYBER_SECURITY_VULNERABILITIES',
        charts
      });
    }
    return treatmentCharts;
  }

  public static buildAiAssessmentStats(aiAssessmentEvaluation: AiAssessmentEvaluation): DetailsChartsDto[] {
    const aiAssessmentStats: DetailsChartsDto[] = [];
    if (aiAssessmentEvaluation?.groups?.length > 0) {
      aiAssessmentEvaluation?.groups.forEach((group: EvalGroup) => {
        let value: number = null;
        if (group.result) {
          value = Math.round(group.result * 100) / 100;
        }
        aiAssessmentStats.push({
          label: of(group.label),
          textValue: of(value ? value + '' : ND),
          value: value,
          max: 5
        });
      });
    }
    return aiAssessmentStats;
  }

  public static buildCovidAssessmentStats(covidAssessmentEvaluation: CovidAssessmentEvaluation): DetailsChartsDto[] {
    const covidAssessmentStats: DetailsChartsDto[] = [];
    if (covidAssessmentEvaluation?.groups?.length > 0) {
      covidAssessmentEvaluation?.groups.forEach((group: EvalGroup) => {
        let value: number = null;
        if (group.result) {
          value = Math.round(group.result * 100) / 100;
        }
        covidAssessmentStats.push({
          label: of(group.label),
          textValue: of(value ? value + '' : ND),
          value: value,
          max: 5
        });
      });
    }
    return covidAssessmentStats;
  }
}
